import { useCallback, useState } from 'react';

interface FilterMenuOpenState {
  anchorEl: HTMLElement;
  initialFieldId?: string;
}

/**
 * Host wiring for FilterMenuPopover: which anchor it's open on and whether it
 * opened straight into one field's editor (chip edit). Transient UI state
 * only — filter *values* never live here.
 *
 * Spread `popoverProps` onto FilterMenuPopover; call `toggleMenu` from the
 * primary trigger, `openMenu` from alternate anchors / F-key handlers, and
 * `openEditor` from AppliedFilterChips' onEdit.
 */
export const useFilterMenuState = () => {
  const [state, setState] = useState<FilterMenuOpenState | null>(null);

  const openMenu = useCallback((anchorEl: HTMLElement) => {
    setState({ anchorEl });
  }, []);

  const toggleMenu = useCallback((anchorEl: HTMLElement) => {
    setState(current =>
      current?.anchorEl === anchorEl && current.initialFieldId === undefined ? null : { anchorEl },
    );
  }, []);

  const openEditor = useCallback((fieldId: string, anchorEl: HTMLElement) => {
    setState({ anchorEl, initialFieldId: fieldId });
  }, []);

  const close = useCallback(() => {
    setState(null);
  }, []);

  return {
    isOpen: state !== null,
    anchorEl: state?.anchorEl ?? null,
    initialFieldId: state?.initialFieldId,
    openMenu,
    toggleMenu,
    openEditor,
    close,
    popoverProps: {
      open: state !== null,
      anchorEl: state?.anchorEl ?? null,
      initialFieldId: state?.initialFieldId,
      onClose: close,
    },
  };
};
